import { observable, action, computed } from "mobx"
import React from 'react'
import { getFileHash, fixProgress, reTry, resetTimes } from "../tools/uploadTools"
import {
  confirmUpload, initUpload, loadGroupList,
  createGroup, loadGroup, getBatchNumber, searchGroups, confirmBatchComplete
} from "../tools/uploadRequest"
import { uploadFile as uploadWithSDK } from "../tools/uploadWithSDK"
import Group from "./Group"
import File, { SUCCESS, EXCEPTION, ACTIVE, INACTIVE } from "./File"
import { getCurrentApp } from "app/api/localTools"
import request from "app/vendor/request"
import config from "app/config"
import { message } from "antd"
export const TASK_TYPE = {
  file: "pgx_file",
  group: "pgx_group"
}

export default class TaskModel {
  @observable id = ""
  @observable batchNumber = ""
  @observable chunkPercent = 0
  @observable groups = []
  @observable searchedGroups = []
  @observable selectedGroupId
  @observable status = INACTIVE
  @observable waitingTime = -1
  @observable errorVisible = false
  @observable errorMessage = ''
  interval = null
  currentGroup = null

  constructor(id, allowSuffix) {
    this.id = id
    this.allowSuffix = allowSuffix
  }

  @action loadBatchNumber = () => {
    getBatchNumber()
      .then(res =>
        this.batchNumber = res.batchNum
        , error =>
          message.error(error.message)
      )
  }

  /*
   * load groups of app
   */
  @action loadGroups = () => {
    loadGroupList(this.id)
      .then(result => {
        this.initGroups(result)
      }, error => {
        message.error(error.message)
      })
  }

  @action searchGroups = (keyword) => {
    searchGroups(this.id, keyword)
      .then(result => {
        this.searchedGroups = result.map(group => ({ groupInfo: group, files: [] }))
      }, error => {
        message.error(error.message)
      })
  }

  /*
   * load a group with groupId(familyId) when singleGroup
   */
  @action loadGroup = (groupId) => {
    loadGroup(groupId)
      .then(result => {
        this.initGroups([result])
        this.setSelectedId(result.id)
      })
  }

  @action initGroups = (result) => {
    result.forEach((groupInfo, index) => {
      this.groups.push(new Group(groupInfo))
    })
  }

  @action addGroupItem = (keyword) => {
    createGroup(keyword, this.id)
      .then(result => {
        this.unshiftGroup(result)
        this.setSelectedId(result.id)
      }, error => {
        message.error(error.message, 3)
      })
  }

  @action updateSelected = (groupInfo, verifyLimsPgdFamily) => {
    this.setSelectedId(groupInfo.id, verifyLimsPgdFamily)
    if (!this.selectedGroup) {
      this.unshiftGroup(groupInfo)
    }
    this.handleJudge(verifyLimsPgdFamily)
  }

  @action handleJudge = (verifyLimsPgdFamily) => {
    const app = getCurrentApp()
    if (app === 'pgd' && verifyLimsPgdFamily) {
      this.selectedGroup.getJudgeData()
    } else if (app === 'pgd' && !verifyLimsPgdFamily) {
      this.selectedGroup.getSamplesInfo()
    }
  }

  @action unshiftGroup = (groupInfo) => {
    this.groups.unshift(new Group(groupInfo))
  }

  @action addFile = (file) => {
    this.selectedGroup.add(new File(file))
  }

  // 从上传列表中移除文件
  @action removeFile = (file) => {
    this.selectedGroup.remove(file)
  }

  @action removeTask = (task) => {
    return task.type === TASK_TYPE.file
      ? this.removeFileTask(task)
      : this.removeGroupTask(task)
  }

  @action removeTimer = () => {
    clearInterval(this.interval)
  }

  @computed get waiting() {
    return this.waitingTime !== -1
  }

  @action countDown = (duration, callback) => {
    this.waitingTime = duration
    this.interval = setInterval(() => {
      this.descendWaitingTime(callback)
    }, 1000)
  }

  @action descendWaitingTime = (callback) => {
    if (this.waitingTime > -1) {
      this.waitingTime--
    } else {
      this.removeTimer()
      callback()
    }
  }

  // 支持不同类型任务异常的分离处理
  // 进度列表中移除任务
  @action removeFileTask = task => {
    return new Promise((resolve, reject) => {
      const belongedGroup = this.groups.find(group => group.files.indexOf(task) > -1)
      if (task.isInactived || task.isError) {
        belongedGroup.remove(task)
        resolve("success")
      } else {
        reject("该任务状态已改变，无法删除")
      }
    })
  }

  @action addFileTask = (groupTask, file) => {
    return new Promise((resolve, reject) => {
      if (!groupTask.isSuccess) {
        groupTask.push(new File(file))
        resolve("success")
      } else {
        reject("该任务已完成，无法继续添加")
      }
    })
  }

  // 全部失败、小组未激活
  @action removeGroupTask = task => {
    const group = this.groups.find(group => group.groupInfo.name === task.name)
    return new Promise((resolve, reject) => {
      if (task.unReachable) {
        reject("该任务状态已改变，无法删除")
      } else {
        group.clear()
        resolve("success")
      }
    })
  }

  @action updateShortName = (index, value) => {
    if (this.selectedGroup) {
      this.selectedGroup.files[index].updateShortName(value)
    }
  }

  @action setSelectedId = (id, verifyLimsPgdFamily) => {
    this.selectedGroupId = id
    if (!id) {
      return
    }
  }

  @action checkDuplicate = (group, newFile) => {
    return group.files.some(fileModel => this.compare(newFile, fileModel.file))
  }

  compare = (file1, file2) => (
    file1.name === file2.name
    && file1.lastModified === file2.lastModified
    && file1.size === file2.size
  )

  @computed get preparedGroups() {
    return this.groups.filter(group => group.files.length !== 0)
  }

  /*
   * return currentGroup in groups
   * slice() 为了让 computed 跟踪 array
   */
  @computed get selectedGroup() {
    return this.groups.find(group => group.groupInfo.id === this.selectedGroupId)
  }

  @computed get isPending() {
    return this.status === ACTIVE
  }

  @computed get isSuccess() {
    return this.status === SUCCESS
  }

  @computed get isError() {
    return this.status === EXCEPTION
  }

  @computed get progress() {
    let progress = this.preparedGroups.reduce((sum, group) =>
      sum + group.progress * (group.chunkCount / this.chunkCount),
      0)
    return fixProgress(progress)
  }

  @computed get chunkCount() {
    return this.groups.reduce(
      (sum, group) => sum + group.chunkCount,
      0
    )
  }

  @computed get hasTracer() {
    let hasTracerFiles = []
    this.groups.map(group => {
      return group.files.map(file => {
        if (file.tracer && file.tracer !== '') {
          hasTracerFiles.push(file)
        }
        return null //handle eslint warning
      })
    })
    return hasTracerFiles.length
  }

  @action generateUUID = () => {
    var d = new Date().getTime()
    if (window.performance && typeof window.performance.now === "function") {
      d += performance.now()
    }
    var uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (d + Math.random() * 16) % 16 | 0
      d = Math.floor(d / 16)
      return (c === 'x' ? r : (r && 0x3 | 0x8)).toString(16)
    })
    return uuid.split('-').join('')
  }

  @action handleEcsFile = () => {
    this.preparedGroups.forEach(group => {
      let doubles = [], singles = [], uuidArr = []
      group.files.forEach(file => {
        file.paired === 2 ? doubles.push(file) : singles.push(file)
      })

      for (let i = 0; i < doubles.length / 2; i++) {
        uuidArr.push(this.generateUUID())
      }

      doubles.sort((a, b) => {
        return a.name.localeCompare(b.name)
      })

      this.addEcsUUID(doubles, singles, uuidArr)
      group.files = doubles.concat(singles)
    })
  }

  @action addEcsUUID = (doubles, singles, uuidArr) => {
    doubles.forEach((file, index) => {
      file.pairedEnd = uuidArr[parseInt(index / 2, 10)]
    })

    singles.map(file => file.pairedEnd = this.generateUUID())
  }

  @computed get filesCount() {
    return this.groups.reduce(
      (sum, group) => sum + group.files.length,
      0)
  }

  @computed get failedCount() {
    return this.groups.reduce((sum, group) => sum + group.failedData.count, 0)
  }

  @computed get successCount() {
    return this.groups.reduce((sum, group) => sum + group.successCount, 0)
  }

  @computed get failedData() {
    return this.groups.filter(group => group.failedData.count > 0)
      .map(group => group.failedData)
  }

  @action startUpload(type) {
    resetTimes()
    return new Promise((resolve, reject) => {
      if (this.filesCount === 0) {
        reject("您还没有上传任何文件^_^")
      } else if (this.filesCount !== this.hasTracer && this.hasTracer !== 0) {
        reject("请填写标记分子^_^")
      } else {
        getCurrentApp() === 'ecs' && this.handleEcsFile()
        this.status = ACTIVE
        this.currentGroup = this.preparedGroups[0]
        if(type === 1){//强制上传
          this.uploadFile(this.preparedGroups[0].files[0])
          resolve("开始上传")
        }else{
          this.fileNamesVerify().then(
            res => {
              if (res.code === 200){
                this.uploadFile(this.preparedGroups[0].files[0])
                resolve("开始上传")
              }else{
                this.errorVisible=true
                this.errorMessage=res.message
              }
            },
            error => {
                this.errorVisible=true
                this.errorMessage=error.message
            }
          )
        }
      }
    })
  }
  //验证文件名是否符合
  @action fileNamesVerify() {
    try {
      const files = this.preparedGroups[0].files || []
      const references = files.filter(file => file.name).map(file => file.name)
      const data={
        fileNames:references,
        appId:this.preparedGroups[0].groupInfo.appId
      }
      const groupId=this.preparedGroups[0].groupInfo.id
      const result =  request.post(`${config.backend}/${getCurrentApp()}/datas/check/${groupId}`)
      .send(data)
      .checkToken()
      .commonDispose()
      return Promise.resolve(result)
    } catch (error) {
      return Promise.reject(error)
    }
  }

  @action uploadFile(file) {
    if (file.isSuccess) {
      this.uploadNextFile(file)
      return
    }

    file.setActive()

    // 统一使用ali-oss SDK上传，SDK会自动处理分片
    // 小文件：自动使用简单上传（1个分片）
    // 大文件：自动使用分片上传
    this.uploadFileWithSDK(file)
  }

  /**
   * 使用ali-oss SDK上传文件 (重构后版本)
   */
  @action uploadFileWithSDK = async (file) => {
    // 防止重复上传
    if (file.isUploading) {
      console.log(`⚠️ 文件 ${file.name} 正在上传中，跳过重复上传`)
      return
    }
    
    file.isUploading = true
    let dataId = ""
    
    // 设置文件为激活状态，开始显示进度条
    file.setActive()
    file.updateProgress(0)
    
    console.log(`🚀 开始上传文件: ${file.name}`)
    
    try {
      // 1. 获取文件MD5
      const contentMd5 = await getFileHash(file.file)

      // 2. 初始化上传记录，获取 dataId
      const initResult = await initUpload(
        file, 
        file.name, 
        contentMd5, 
        this.id, 
        this.batchNumber, 
        this.currentGroup.referenceCount
      )
      dataId = initResult.id

      // 3. 检查是否可以秒传
      if (initResult.exist) {
        // 如果可以秒传，直接调用 confirmUpload 并结束
        console.log(`✅ 文件 ${file.name} 可秒传，dataId: ${dataId}`)
        await this.confirmUpload(dataId, file, true, null) // 秒传不需要 uploadResult
        return // 秒传流程结束
      }

      // 4. 如果不能秒传，则执行真正的上传
      console.log(`🚀 开始为 ${file.name} 调用SDK上传...`)
      const uploadResult = await uploadWithSDK(file.file, {
        appType: getCurrentApp(), // 传递应用类型，用于生成对应的目录
        onProgress: (progress) => {
          // 这里只负责更新UI，不处理任何业务逻辑
          if (progress.isNormal) {
            file.updateProgress(fixProgress(progress.value))
          }
        }
        // 注意：这里不再需要 onSuccess 和 onError 回调了
      })

      // 5. 上传成功后，调用 confirmUpload
      console.log(`✅ SDK上传成功，准备确认:`, uploadResult)
      await this.confirmUpload(dataId, file, false, uploadResult)

    } catch (error) {
      // 统一的错误处理
      console.error(`❌ 上传流程中发生错误 for ${file.name}:`, error)
      file.isUploading = false // 重置上传状态
      
      // 如果是秒传检查失败，则直接重试确认步骤
      if (error && error.isQuickUpload) {
        console.log(`秒传检查失败，尝试重新确认...`)
        this.reConfirmUpload(dataId, file, true, null, error.message)
      } else {
        // 如果是上传或初始化失败，则重试整个上传过程
        this.reUpload(file, error && error.message)
      }
    }
  }

  // 已删除 uploadFileWithLegacy 方法 - 统一使用SDK上传

  /**
   * 确认上传。
   * @param dataId
   * @param file
   * @param isQuickUpload
   * @param uploadResult - { name: string, url: string, size: number, ... }
   */
  async confirmUpload(dataId, file, isQuickUpload = false, uploadResult = null) {
    try {
      // confirmUpload 请求现在需要接收 uploadResult
      await confirmUpload(dataId, uploadResult)
      
      file.updateProgress(100)
      file.setSuccess()
      if (isQuickUpload) {
        file.setQuickUpload()
      }
      this.uploadNextFile(file)

      // (可选) 检查是否所有文件都上传完毕，并触发批量完成
      if (this.successCount === this.filesCount && getCurrentApp() === 'ecs') {
        confirmBatchComplete(this.batchNumber).catch(err => message.error(err))
      }
    } catch (error) {
      // 如果 confirmUpload 失败，则触发重试机制
      this.reConfirmUpload(dataId, file, isQuickUpload, uploadResult, error && error.message)
    }
  }

  reConfirmUpload(dataId, file, isQuickUpload = false, uploadResult = null, message) {
    reTry(() => this.confirmUpload(dataId, file, isQuickUpload, uploadResult), message) // 将 uploadResult 传递下去
      .catch(() => this.disableFile(file))
  }

  reUpload(file, message) {
    reTry(() => this.uploadFile(file), message)
      .then(null, this.disableTask)
  }

  disableTask = () => {
    this.status = EXCEPTION
    this.groups.forEach(group => {
      group.files.forEach(file => {
        file.isActived && file.setError('网络异常，请检查网络')
      })
    })
  }

  disableFile = (file, message) => {
    file.setError(message)
    this.uploadNextFile(file)
  }

  @action uploadNextFile(file) {
    const nextFile = this.getNextFile(file, this.preparedGroups)
    if (!nextFile || !this.isPending) {
      return
    } else {
      this.uploadFile(nextFile)
    }
  }

  @action getNextFile(file, groups) {
    const currentFiles = this.currentGroup.files
    const currentFileIndex = currentFiles.indexOf(file)
    const currentGroupIndex = groups.indexOf(this.currentGroup)

    if (currentGroupIndex < groups.length) {
      if (currentFileIndex + 1 < currentFiles.length) {
        return currentFiles[currentFileIndex + 1]
      } else if (currentGroupIndex === groups.length - 1) {
        this.status = SUCCESS
      } else {
        this.currentGroup = groups[currentGroupIndex + 1] // 更新当前激活的组
        return groups[currentGroupIndex + 1].files[0]
      }
    }
  }
}

const AZF_Miseq = '302'
export class AzfTaskModel extends TaskModel {
  constructor(id, allowSuffix) {
    super(id, allowSuffix)
    this.isMiseq = id === AZF_Miseq
  }

  @action sortFiles = () => {
    this.preparedGroups.forEach(group => {
      const references = [], rest = []
      group.files.forEach(file => {
        if (file.reference) {
          references.push(file)
        } else {
          rest.push(file)
        }
      })
      if (references.length > 0) {
        group.files = references.concat(rest)
      }
    })
  }

  @action validatePgmReference = () => {
    let referenceMessage = []
    this.preparedGroups.forEach(group => {
      const referenceLength = group.files.filter(file => file.reference).length
      if (referenceLength < 1) {
        referenceMessage.push(group.groupInfo.name)
      }
    })
    return referenceMessage
  }

  validateGroup = () => {
    let referenceMessage = [],
      fileMessage = []

    this.preparedGroups.forEach(group => {
      const referenceLength = group.files.filter(file => file.reference).length
      if (referenceLength <= 1) {
        referenceMessage.push(<span><br />{group.groupInfo.name}</span>)
      }
      if (group.files.length === 1) {
        fileMessage.push(<span><br />{group.groupInfo.name}</span>)
      }
    })
    let msg = <span>{fileMessage.length > 0 && '以下组至少上传两个文件'}{fileMessage.length > 0 && fileMessage}<br />
      {referenceMessage.length > 0 && '以下组至少选择两个参照'}{referenceMessage.length > 0 && referenceMessage}</span>
    if (referenceMessage.length > 0 || fileMessage.length > 0) {
      throw msg
    }
  }

  // 有关继承，只能用 function
  @action async startUpload(type) {
    this.isMiseq && this.validateGroup()
    this.sortFiles()
    return await super.startUpload(type)
  }
}



// WEBPACK FOOTER //
// ./src/app/components/normal/BasicModal/UploadModal/stores/TaskModel.js